import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Image, Wrench, Trash2, ChevronDown } from "lucide-react";
import { getCategoryFallbackImage } from "@/lib/categoryFallbackImage";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export type ApplianceCategory = "washing-machine" | "vacuum-cleaner" | "dishwasher" | "refrigerator" | "oven" | "dryer" | "other";

interface ApplianceCardProps {
  name: string;
  modelNumber: string;
  category: ApplianceCategory;
  imageUrl?: string;
  onClick?: () => void;
  onDelete?: () => void;
  onCategoryChange?: (category: ApplianceCategory) => void;
}

// Category labels are now translated via t('appliance:categories.<key>').
// This object is kept only as a type-safe key map; values are intentionally placeholders.
const categoryKeys: Record<ApplianceCategory, string> = {
  "washing-machine": "washing-machine",
  "vacuum-cleaner": "vacuum-cleaner",
  "dishwasher": "dishwasher",
  "refrigerator": "refrigerator",
  "oven": "oven",
  "dryer": "dryer",
  "other": "other",
};

const categoryStyles: Record<ApplianceCategory, string> = {
  "washing-machine": "category-badge-washing",
  "vacuum-cleaner": "category-badge-vacuum",
  "dishwasher": "category-badge-washing",
  "refrigerator": "category-badge-vacuum",
  "oven": "category-badge-washing",
  "dryer": "category-badge-washing",
  "other": "category-badge-vacuum",
};

const allCategories: ApplianceCategory[] = [
  "washing-machine",
  "vacuum-cleaner", 
  "dishwasher",
  "refrigerator",
  "oven",
  "dryer",
  "other",
];

export function ApplianceCard({ name, modelNumber, category, imageUrl, onClick, onDelete, onCategoryChange }: ApplianceCardProps) {
  const { t } = useTranslation("appliance");
  const categoryLabel = (cat: ApplianceCategory) => t(`categories.${categoryKeys[cat]}`);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = () => {
    onDelete?.();
    setShowDeleteDialog(false);
  };

  const handleCategoryClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleCategorySelect = (e: React.MouseEvent, newCategory: ApplianceCategory) => {
    e.stopPropagation();
    onCategoryChange?.(newCategory);
  };

  return (
    <>
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent onClick={(e) => e.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("card.deleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              <Trans
                i18nKey="card.deleteConfirm"
                ns="appliance"
                values={{ name }}
                components={{ 1: <strong /> }}
              />
              <br /><br />
              {t("card.deletePermanent")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("card.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("card.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="appliance-card relative" onClick={onClick}>
        {onDelete && (
          <button
            onClick={handleDeleteClick}
            className="absolute top-2 right-2 p-1.5 rounded-md bg-destructive/10 hover:bg-destructive/20 text-destructive transition-colors"
            aria-label={t("card.deleteAriaLabel")}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center overflow-hidden flex-shrink-0">
        {imageUrl ? (
          <img src={imageUrl} alt={name} className="w-[110%] h-[110%] object-contain" />
        ) : getCategoryFallbackImage(category) ? (
          <img src={getCategoryFallbackImage(category)!} alt={name} className="w-3/4 h-3/4 object-contain opacity-70" />
        ) : (
          <Image className="w-8 h-8 text-muted-foreground" />
        )}
      </div>
      
      <div className="flex-1 min-w-0">
        <h3 className="font-semibold text-foreground truncate">{name}</h3>
        <p className="text-sm text-muted-foreground">{modelNumber}</p>
        <div className="flex items-center gap-2 mt-1.5">
          {onCategoryChange ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={handleCategoryClick}>
                <button className={`category-badge ${categoryStyles[category]} inline-flex items-center gap-1 cursor-pointer hover:opacity-80 transition-opacity`}>
                  {categoryLabel(category)}
                  <ChevronDown className="w-3 h-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="min-w-[160px]" onClick={(e) => e.stopPropagation()}>
                {allCategories.map((cat) => (
                  <DropdownMenuItem
                    key={cat}
                    onClick={(e) => handleCategorySelect(e, cat)}
                    className={cat === category ? "bg-accent" : ""}
                  >
                    {categoryLabel(cat)}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <span className={`category-badge ${categoryStyles[category]}`}>
              {categoryLabel(category)}
            </span>
          )}
          <Wrench className="w-4 h-4 text-muted-foreground" />
        </div>
      </div>
      </div>
    </>
  );
}
